import React, { FC, useState, useCallback, useEffect } from "react";
import {
    Button,
    OnboardingAside,
    OnboardingContent,
    FieldCheckbox,
    Input,
    Icon,
    LoadingSpinner,
} from "@xapp/components";

import { ContactCaptureData, ContactCaptureService } from "@xapp/contact-capture-handler";

import { applyEmailCapture, deriveEmailEnabled } from "components/Onboarding/utils/contactCaptureEmail";

export interface DataCollectionStepProps {
    /**
     * Optional style applied to the root div
     */
    style?: React.CSSProperties;
    /**
     * Optional class name to apply to the root div
     */
    className?: string;

    aside?: React.ReactNode;

    value?: ContactCaptureData;

    /**
     * Callback when user clicks continue
     */
    onNext?: (value?: ContactCaptureData) => void;

    /**
     * Callback when user clicks back
     */
    onBack?: () => void;

    /**
     * Is the form currently saving?
     */
    saving?: boolean;

    /**
     * Loading state while fetching data
     */
    loading?: boolean;

    /**
     * Whether email capture is available for this tier (from AppLimitations.captureLead).
     * Defaults to true when not provided.
     */
    emailCaptureAvailable?: boolean;
}

// Default service options based on the user's request
const DEFAULT_SERVICE_OPTIONS: ContactCaptureService[] = [
    {
        id: "schedule_visit",
        label: "Schedule Visit",
        requiresDate: true,
    },
    {
        id: "get_quote",
        label: "Get Quote",
        requiresDate: true,
    },
    {
        id: "contact_us",
        label: "Contact Us",
        requiresDate: false,
    },
];

export const DataCollectionStep: FC<DataCollectionStepProps> = (props) => {
    const { aside, value, onNext, onBack, loading, saving, emailCaptureAvailable = true } = props;

    // Email capture defaults to enabled — prominently promoted per spec
    const [emailEnabled, setEmailEnabled] = useState<boolean>(() => deriveEmailEnabled(value));

    // Initialize state from value prop or use defaults
    const [serviceOptions, setServiceOptions] = useState<ContactCaptureService[]>(() => {
        if (value?.capture?.serviceOptions) {
            return value.capture.serviceOptions.map((option) => ({
                id: option.id,
                label: option.label,
                requiresDate: option.requiresDate || false,
            }));
        }
        return DEFAULT_SERVICE_OPTIONS;
    });

    // Update state when value prop changes
    useEffect(() => {
        if (value?.capture?.serviceOptions && value.capture.serviceOptions.length > 0) {
            setServiceOptions(
                value.capture.serviceOptions.map((option) => ({
                    id: option.id,
                    label: option.label,
                    requiresDate: option.requiresDate || false,
                }))
            );
        }

        // Always sync so an absent EMAIL descriptor resets the toggle to its default
        // rather than retaining stale state from a previous value.
        setEmailEnabled(deriveEmailEnabled(value));
    }, [value]);

    const handleServiceDateRequirement = useCallback((index: number, requiresDate: boolean) => {
        setServiceOptions((prev) => prev.map((service, i) => (i === index ? { ...service, requiresDate } : service)));
    }, []);

    const handleServiceLabelChange = useCallback((index: number, label: string) => {
        setServiceOptions((prev) =>
            prev.map((service, i) =>
                i === index ? { ...service, label, id: label.toLowerCase().replace(/\s/g, "_") } : service
            )
        );
    }, []);

    const handleDeleteService = useCallback(
        (index: number) => {
            // Don't allow deletion if there are only 2 services left
            if (serviceOptions.length > 2) {
                setServiceOptions((prev) => prev.filter((_, i) => i !== index));
            }
        },
        [serviceOptions.length]
    );

    const handleContinue = useCallback(() => {
        onNext?.(
            applyEmailCapture(
                value,
                emailEnabled,
                serviceOptions.map((option) => ({
                    id: option.id,
                    label: option.label,
                    requiresDate: option.requiresDate,
                }))
            )
        );
    }, [onNext, value, serviceOptions, emailEnabled]);

    // Validation: at least one service option
    const isValid = serviceOptions.length >= 2;

    return (
        <>
            {aside && <OnboardingAside>{aside}</OnboardingAside>}

            <OnboardingContent>
                {loading || saving ? (
                    <div
                        style={{
                            padding: "40px",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: "400px",
                        }}
                    >
                        <LoadingSpinner
                            textStyle={{ fontSize: "1em" }}
                            loadingText={saving ? "Saving configuration..." : "Loading configuration..."}
                        />
                    </div>
                ) : (
                    <div style={{ padding: "10px", width: "100%" }}>
                        {/* Email Capture — prominently promoted, defaults to enabled */}
                        <div
                            style={{
                                marginBottom: "24px",
                                padding: "16px",
                                border: "2px solid #1AA7DC",
                                borderRadius: "8px",
                                backgroundColor: "#e8f7fc",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: "12px",
                            }}
                        >
                            <div>
                                <div style={{ fontWeight: 600, fontSize: "15px", color: "#1A3C5E" }}>
                                    Capture Email Addresses
                                </div>
                                <div style={{ fontSize: "13px", color: "#555", marginTop: "4px" }}>
                                    Collect email from every lead so you can follow up directly.
                                </div>
                            </div>
                            <FieldCheckbox
                                value={emailEnabled}
                                onChange={setEmailEnabled}
                                disabled={!emailCaptureAvailable}
                            />
                        </div>

                        {/* Service Options */}
                        <div style={{ marginBottom: "30px" }}>
                            <h3 style={{ marginBottom: "15px" }}>Which contact options do you want to offer?</h3>
                            <p style={{ fontSize: "14px", color: "#666", marginBottom: "20px" }}>
                                Edit the names to match your services.
                            </p>

                            <div
                                style={{
                                    border: "1px solid #e0e0e0",
                                    borderRadius: "8px",
                                    overflow: "hidden",
                                }}
                            >
                                {/* Table Header */}
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 100px 60px",
                                        gap: "15px",
                                        padding: "15px",
                                        backgroundColor: "#f5f5f5",
                                        borderBottom: "1px solid #e0e0e0",
                                        fontWeight: "bold",
                                        fontSize: "14px",
                                    }}
                                >
                                    <div>Option</div>
                                    <div style={{ textAlign: "center" }}>Show Calendar</div>
                                    <div style={{ textAlign: "center" }}>Delete</div>
                                </div>

                                {/* Table Rows */}
                                {serviceOptions.map((service, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "1fr 100px 60px",
                                            gap: "15px",
                                            padding: "15px",
                                            backgroundColor: "#fff",
                                            borderBottom:
                                                index < serviceOptions.length - 1 ? "1px solid #e0e0e0" : "none",
                                            alignItems: "center",
                                        }}
                                    >
                                        <div>
                                            <Input
                                                value={service.label}
                                                onChange={(value) => handleServiceLabelChange(index, String(value))}
                                                style={{ width: "100%" }}
                                            />
                                        </div>
                                        <div style={{ display: "flex", justifyContent: "center" }}>
                                            <FieldCheckbox
                                                value={service.requiresDate}
                                                onChange={(requiresDate) =>
                                                    handleServiceDateRequirement(index, requiresDate)
                                                }
                                            />
                                        </div>
                                        <div style={{ display: "flex", justifyContent: "center" }}>
                                            <button
                                                onClick={() => handleDeleteService(index)}
                                                disabled={serviceOptions.length <= 2}
                                                style={{
                                                    background: "none",
                                                    border: "none",
                                                    cursor: serviceOptions.length > 2 ? "pointer" : "not-allowed",
                                                    opacity: serviceOptions.length > 2 ? 1 : 0.3,
                                                    padding: "4px",
                                                    borderRadius: "4px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                                title={
                                                    serviceOptions.length <= 2
                                                        ? "Minimum 2 services required"
                                                        : "Delete service"
                                                }
                                            >
                                                <Icon value="delete" style={{ color: "#dc3545", fontSize: "18px" }} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                marginTop: "40px",
                            }}
                        >
                            {onBack && <Button label="Back" variation="secondary" onClick={onBack} />}
                            <Button
                                label="Continue"
                                disabled={!isValid}
                                onClick={handleContinue}
                                style={{ marginLeft: "auto" }}
                            />
                        </div>
                    </div>
                )}
            </OnboardingContent>
        </>
    );
};
